import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of, ReplaySubject, Subscription, timer } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IframeDialogComponent } from '@app/_dialogs/iframe-dialog/iframe-dialog.component';
import { AdminInfoDto } from '@app/_models/adminInfoDto';
import { ApplicationConfig } from '@app/_models/applicationConfigDto';
import { ApplicationStatus } from '@app/_models/applicationStatusDto';
import { CommandResult } from '@app/_models/command/commandResult';
import { AccountService } from './account.service';
import { BaseApiService } from './baseApi.service';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly accountService = inject(AccountService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(BsModalService);
  private readonly destroyRef = inject(DestroyRef);

  private static applicationStatusSubscription?: Subscription;
  private readonly currentApplicationStatus = new ReplaySubject<ApplicationStatus | undefined>(1);
  currentApplicationStatus$ = this.currentApplicationStatus.asObservable();

  constructor() {
    super('application');
  }

  getAdminInfo(): Observable<AdminInfoDto> {
    return this.http.get<AdminInfoDto>(this.baseControllerUrl + '/admininfo');
  }

  getApplicationStatus(): Observable<ApplicationStatus> {
    return this.http.get<ApplicationStatus>(this.baseControllerUrl + '/status');
  }

  setApplicationStatus(applicationStatus: ApplicationStatus): Observable<CommandResult> {
    return this.http.post<CommandResult>(this.baseControllerUrl + '/status', applicationStatus);
  }

  private readonly applicationConfig$ = this.http
    .get<ApplicationConfig>(this.baseControllerUrl + '/config')
    .pipe(shareReplay(1));

  getApplicationConfig(): Observable<ApplicationConfig> {
    return this.applicationConfig$;
  }

  lockoutText(): Observable<string | undefined> {
    return this.currentApplicationStatus$.pipe(
      map((status) => {
        if (status?.userLockoutEnabled && status.lockoutText) {
          return status.lockoutText;
        }
        return undefined;
      }),
    );
  }

  /*
   * Load application status every minute.
   */
  subscribeApplicationStatus(): void {
    if (ApplicationService.applicationStatusSubscription) {
      return;
    }

    ApplicationService.applicationStatusSubscription = timer(0, 60000)
      .pipe(
        switchMap(() => {
          return this.getApplicationStatus().pipe(
            catchError((err) => {
              // Handle errors
              console.error(err);
              return of(undefined);
            }),
          );
        }),
        filter((data) => data !== undefined),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.currentApplicationStatus.next(data);
        if (data?.userLockoutEnabled && this.accountService.userRoles?.indexOf('Admin') == -1) {
          this.accountService.logout(); // Logout all non admin users.
        }
      });
  }

  unsubscribeApplicationStatus(): void {
    if (ApplicationService.applicationStatusSubscription) {
      ApplicationService.applicationStatusSubscription.unsubscribe();
      ApplicationService.applicationStatusSubscription = undefined;
    }
  }

  showInvoiceInfoDialog(): void {
    this.currentApplicationStatus$.pipe(take(1)).subscribe((applicationStatus) => {
      if (applicationStatus?.invoiceInfoDialogPage) {
        const dontShowInvoiceInfoDialogPage = localStorage.getItem('dontShowInvoiceInfoDialogPage');

        if (applicationStatus.invoiceInfoDialogPage != dontShowInvoiceInfoDialogPage) {
          IframeDialogComponent.showDialogTranslated(
            this.translate,
            this.modalService,
            applicationStatus.invoiceInfoDialogPage,
            undefined,
            undefined,
            'Global.Close',
            undefined,
            'modal-1000',
            undefined,
            'Global.DontShowAgain',
          ).then((modalRef) => {
            modalRef.content?.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((onCloseResult) => {
              if (onCloseResult.checkboxValue) {
                if (applicationStatus.invoiceInfoDialogPage) {
                  localStorage.setItem('dontShowInvoiceInfoDialogPage', applicationStatus.invoiceInfoDialogPage);
                }
              }
            });
          });
        }
      }
    });
  }
}
